import React, { useEffect, useMemo, useState } from "react";

// MUI
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { DataGrid } from "@mui/x-data-grid";
import apiClient from '../ApiClient/ApiClient';
/**
 * 售價一覽表（改版）
 * - 固定上方查詢列
 * - 左側 70%：主表（料品清單） DataGrid
 * - 右側 30%：調價紀錄 DataGrid
 * - 編輯改用 Dialog 小視窗
 * - 取消上下頁，改為一次顯示查詢結果
 * - 匯出：一覽表（依查詢條件）、單一圖號調價紀錄
 */

export default function Item({ data }) {
  // 查詢條件
  const [itemName, setItemName] = useState("");
  const [client, setClient] = useState("");
  const [price, setPrice] = useState("");
  const [drawNum, setDrawNum] = useState("");
  const [enddrawNum, setendDrawNum] = useState("");

  // 載入遮罩
  const [loading, setLoading] = useState(false);

  // 主表、細表資料
  const [mainRows, setMainRows] = useState([]);   // items
  const [detailRows, setDetailRows] = useState([]); // sellPriceList

  // 目前選取的圖號（用來載入調價紀錄）
  const [selectedDrawNum, setSelectedDrawNum] = useState("");

  // 編輯對話框（主表）
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  // 右側 DataGrid：暫存被編輯的那一列（避免未按「保存」就呼叫 API）
  const [pendingDetailEdit, setPendingDetailEdit] = useState(null);
  const [canEditDetail, setCanEditDetail] = useState(false);

  // 若父層有預先塞 data
  useEffect(() => {
    if (!data) return;
    setMainRows(normalizeMainList(data));
  }, [data]);

  // ---------- 正規化 ----------
  function normStatus(res) {
    return res?.statusCode ?? res?.StatusCode;
  }
  function normalizeMainList(res) {
    if (!res) return [];
    const list = Array.isArray(res.items)
      ? res.items
      : res.item
      ? [res.item]
      : [];
    return list.map((x, idx) => ({ ...x, id: x.id ?? `${x.drawNum}-${x.client}-${idx}` }));
  }
  function normalizeDetailList(res) {
    const list = res?.sellPriceList ?? [];
    return Array.isArray(list)
      ? list.map((x, idx) => {
          const currency = String(x.currency ?? "").trim().toUpperCase();
          return { ...x, currency, id: x.id ?? `${x.drawNum}-${x.date}-${x.price}-${idx}` };
        })
      : [];
  }
  function currencySymbol(c) {
    if (!c) return "";
    switch (String(c).trim().toUpperCase()) {
      case "TWD":
        return "NT$";
      case "USD":
        return "USD$";
      case "CNY":
        return "CNY¥";
      case "PHP":
        return "PHP₱";
      case "EUR":
        return "EUR€";
      case "HKD":
        return "HK$";
      case "AUD":
        return "A$";
      default:
        return "";
    }
  }

  const endDrawNumRef = React.useRef(null);

  const commitStartToEnd = (val) => {
    setendDrawNum(val);
    // 可選：自動把游標移到「結束圖號」讓使用者微調
    endDrawNumRef.current?.focus();
  };

  // ---------- API ----------
  const  fetchMain = async () => {
    if (!itemName && !client && !price && !drawNum) {
      alert("搜尋條件不得全部為空");
      return;
    }
    setLoading(true);
    try {
      const resp = await apiClient.get(`/SellPrice/mains`, {
        params: { itemName, client, price: price || 0, drawNum ,enddrawNum},
      });
      if (normStatus(resp.data) === 200) {
        setMainRows(normalizeMainList(resp.data));
        setDetailRows([]);
        setCanEditDetail(false);
        setSelectedDrawNum("");
        alert("搜尋成功");
      } else {
        setMainRows([]);
        setDetailRows([]);
        setCanEditDetail(false);
        setSelectedDrawNum("");
        alert("查無資料");
      }
    } catch (e) {
      console.error(e);
      alert("搜尋失敗");
    } finally {
      setLoading(false);
    }
  };

  const fetchDetail = async (dn) => {
    if (!dn) return;
    setLoading(true);
    try {
      const resp = await apiClient.get(`/SellPrice/detailList`, { params: { drawNum: dn } });
      if (normStatus(resp.data) === 200) {
        setDetailRows(normalizeDetailList(resp.data));
        setCanEditDetail(resp.data?.CanEdit ?? resp.data?.canEdit ?? false);
      } else {
        setDetailRows([]);
        setCanEditDetail(false);
      }
      setSelectedDrawNum(dn);
      setPendingDetailEdit(null); // 切換圖號時清空暫存
    } catch (e) {
      console.error(e);
      setCanEditDetail(false);
      alert("讀取調價紀錄失敗");
    } finally {
      setLoading(false);
    }
  };

  const deleteMain = async (row) => {
    if (!window.confirm(`確定刪除圖號 ${row.drawNum} 的售價資料？`)) return;
    setLoading(true);
    try {
      const resp = await apiClient.get(`/SellPrice/DeleteSellItem`, { params: { drawNum: row.drawNum } });
      if (resp?.data === 200) {
        await fetchMain();
        alert("刪除成功");
      } else {
        alert("刪除失敗，找不到可刪除物件");
      }
    } catch (e) {
      console.error(e);
      alert("刪除失敗");
    } finally {
      setLoading(false);
    }
  };

  const deleteDetail = async (row) => {
    if (!window.confirm(`確定刪除 ${row.date} 的調價紀錄？`)) return;
    setLoading(true);
    try {
      const resp = await apiClient.get(`/SellPrice/DeleteSellPrice`, {
        params: { drawNum: row.drawNum, date: row.date, price: row.price },
      });
      if (resp?.data === 200) {
        await fetchDetail(row.drawNum);
        alert("刪除成功");
      } else {
        alert("刪除失敗，找不到可刪除物件");
      }
    } catch (e) {
      console.error(e);
      alert("刪除失敗");
    } finally {
      setLoading(false);
    }
  };

  const exportSingleExcel = async () => {
    const dn = selectedDrawNum || drawNum;
    if (!dn) {
      alert("請先輸入圖號或在主表選一筆資料");
      return;
    }
    setLoading(true);
    try {
      const resp = await apiClient.get(`/SellPrice/CreateExcel`, {
        params: { drawNum: dn },
        responseType: "blob",
      });
      if (resp?.data) {
        const d = new Date();
        const f = `售價調價紀錄_${dn}_${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(
          d.getDate()
        ).padStart(2, "0")}.xlsx`;
        const url = window.URL.createObjectURL(new Blob([resp.data]));
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", f);
        document.body.appendChild(a);
        a.click();
        a.remove();
        alert("匯出成功");
      } else {
        alert("匯出失敗");
      }
    } catch (e) {
      console.error(e);
      alert("匯出失敗");
    } finally {
      setLoading(false);
    }
  };

  const exportListExcel = async () => {
    if (!itemName && !client && !price && !drawNum) {
      alert("至少輸入一個查詢條件");
      return;
    }
    setLoading(true);
    try {
      const resp = await apiClient.get(`/SellPrice/CreateExcel2`, {
        params: { itemName, client, price: price || 0, drawNum },
        responseType: "blob",
      });
      if (resp?.data) {
        const d = new Date();
        const f = `售價一覽表_${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(
          d.getDate()
        ).padStart(2, "0")}.xlsx`;
        const url = window.URL.createObjectURL(new Blob([resp.data]));
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", f);
        document.body.appendChild(a);
        a.click();
        a.remove();
        alert("匯出成功");
      } else {
        alert("匯出失敗");
      }
    } catch (e) {
      console.error(e);
      alert("匯出失敗");
    } finally {
      setLoading(false);
    }
  };

  // ---------- 右側：編輯保存 ----------
  // ---------- 右側：編輯保存（與進價紀錄一致） ----------
// 3) 編輯提交：在這裡把字串轉成數字，再標記需要保存
const onDetailProcessRowUpdate = (newRow, oldRow) => {
  // 只在提交時才轉成數字，輸入過程保留字串，才不會卡在 56.
  const parsedPrice = toNumberSafe(newRow.price);
  const next = { ...newRow, price: parsedPrice ?? newRow.price }; // 若無法轉就保留原字串

  if (oldRow?.price !== next?.price || oldRow?.currency !== next?.currency) {
    setPendingDetailEdit(next);
  }
  return next;
};


const saveEditedDetail = async () => {
  if (!pendingDetailEdit) return;
  setLoading(true);
  try {
    const numericPrice = toNumberSafe(pendingDetailEdit.price);
    if (numericPrice == null) {
      alert("價格格式不正確");
      setLoading(false);
      return;
    }

    const normalizedCurrency = String(pendingDetailEdit.currency ?? "").trim().toUpperCase();
    const allowedCurrencies = ["TWD", "USD", "PHP", "CNY", "HKD", "AUD", "EUR"];
    if (!allowedCurrencies.includes(normalizedCurrency)) {
      alert("請先設定幣別，再保存最新調價紀錄");
      setLoading(false);
      return;
    }

    const payload = {
      drawNum: pendingDetailEdit.drawNum,
      date: pendingDetailEdit.date,
      price: numericPrice,                 // ★ 確保是數字
      currency: normalizedCurrency,
      pricePerson: sessionStorage.getItem("userName"),
    };

    const resp = await apiClient.post(`/SellPrice/EditDetail`, payload);
    if (resp?.status === 200 || resp?.data === 200) {
      alert("保存成功");
      setPendingDetailEdit(null);
      await fetchMain();
      await fetchDetail(pendingDetailEdit.drawNum);
    } else {
      alert("保存失敗");
    }
  } catch (e) {
    console.error(e);
    alert("保存失敗");
  } finally {
    setLoading(false);
  }
};

// 放在元件外或上方
// 放在元件外或上方（純 JS 版）
const formatYMD = (v) => {
  if (v == null || v === "") return "";
  const s = String(v).trim();

  const d = new Date(s);
  const time = d.getTime();                 // 會是 number 或 NaN
  if (!Number.isNaN(time)) {                // ← 用 Number.isNaN
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}/${m}/${dd}`;
  }

  return s.slice(0, 10).replace(/-/g, "/");
};


  const toNumberSafe = (v) => {
    if (v == null) return null;
    const s = String(v)
      .trim()
      .replace(/[，,]/g, "")   // 去千分位
      .replace(/[。．]/g, "."); // 全形點→半形點
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  // ---------- DataGrid 欄位：主表 ----------
  const mainColumns = useMemo(
    () => [
      { field: "itemName", headerName: "物品名", minWidth: 140, flex: 1 },
      { field: "client", headerName: "客戶", minWidth: 80 },
      {
      field: "price",
      headerName: "價格",
      minWidth: 120,
      flex: 0.6,
      type: "string",        // ★ 把它視為字串編輯
      editable: true,

      // 顯示：前方帶幣別
      valueFormatter: (p) => {
        const r = p.api.getRow(p.id);
        const sym = currencySymbol(r?.currency);
        return p.value == null ? "" : `${sym}${p.value}`;
      },

      // 編輯：用 TextField 接管；同時攔 capture & bubble
      renderEditCell: (params) => {
        const { id, field, value, api } = params;
        const handleChange = (e) => {
          api.setEditCellValue({ id, field, value: e.target.value }, e);
        };
        return (
          <TextField
            size="small"
            type="text"
            autoFocus
            value={value ?? ""}
            inputProps={{
              inputMode: "decimal",           // 手機鍵盤帶小數點
              pattern: "[0-9]*[.,]?[0-9]*",
            }}
            onChange={handleChange}
            onKeyDownCapture={(e) => e.stopPropagation()} // ★ 關鍵1：capture 階段
            onKeyDown={(e) => e.stopPropagation()}        // ★ 關鍵2：bubble 階段
          />
        );
      },
    },
      { field: "drawNum", headerName: "圖號", minWidth: 140, flex: 0.9 },
      {
        field: "actions",
        headerName: "操作",
        sortable: false,
        filterable: false,
        minWidth: 220,
        flex: 0.9,
        renderCell: (p) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              color="success"
              onClick={() => fetchDetail(p.row.drawNum)}
            >
              調價紀錄
            </Button>
            <Button
              variant="contained"
              size="small"
              color="warning"
              onClick={() => {
                setEditRow(p.row);
                setEditOpen(true);
              }}
            >
              新增
            </Button>
            <Button
              variant="contained"
              size="small"
              color="error"
              onClick={() => deleteMain(p.row)}
            >
              刪除
            </Button>
          </Box>
        ),
      },
    ],
    []
  );


  // ---------- DataGrid 欄位：副表 ----------
  // ---------- DataGrid 欄位：副表（調價紀錄） ----------
const detailColumns = useMemo(
  () => [
    {
      field: "date",
      headerName: "日期",
      minWidth: 120,
      flex: 0.7,
      valueFormatter: (params) => formatYMD(params.value),
    
      // （可選）讓排序依實際日期排序，而不是字串 
      sortComparator: (v1, v2) => {
        const t1 = new Date(v1).getTime();
        const t2 = new Date(v2).getTime();
        if (!Number.isNaN(t1) && !Number.isNaN(t2)) {
          return t1 - t2;
        }
        return String(v1 ?? "").localeCompare(String(v2 ?? ""));
      },
      
    },

    {
      field: "price",
      headerName: "價格",
      minWidth: 120,
      flex: 0.6,
      type: "string",        // ★ 把它視為字串編輯
      editable: canEditDetail,

      // 顯示：前方帶幣別
      valueFormatter: (p) => {
        const r = p.api.getRow(p.id);
        const sym = currencySymbol(r?.currency);
        return p.value == null ? "" : `${sym}${p.value}`;
      },

      // 編輯：用 TextField 接管；同時攔 capture & bubble
      renderEditCell: (params) => {
        const { id, field, value, api } = params;
        const handleChange = (e) => {
          api.setEditCellValue({ id, field, value: e.target.value }, e);
        };
        return (
          <TextField
            size="small"
            type="text"
            autoFocus
            value={value ?? ""}
            inputProps={{
              inputMode: "decimal",           // 手機鍵盤帶小數點
              pattern: "[0-9]*[.,]?[0-9]*",
            }}
            onChange={handleChange}
            onKeyDownCapture={(e) => e.stopPropagation()} // ★ 關鍵1：capture 階段
            onKeyDown={(e) => e.stopPropagation()}        // ★ 關鍵2：bubble 階段
          />
        );
      },
    },
    

    {
      field: "currency",
      headerName: "幣別",
      minWidth: 90,
      flex: 0.5,
      type: "singleSelect",
      editable: canEditDetail,
      valueOptions: ["TWD", "USD", "PHP", "CNY","HKD","AUD","EUR"],
      valueFormatter: (p) => {
        const value = String(p.value ?? "").trim();
        return value || "未設定";
      },
    },

    { field: "pricePerson", headerName: "調價人員", minWidth: 90, flex: 0.8 },

    ...(canEditDetail ? [{
      field: "op",
      headerName: "操作",
      sortable: false,
      filterable: false,
      minWidth: 120,
      flex: 0.5,
      renderCell: (p) => (
        <Button
          variant="contained"
          size="small"
          color="error"
          onClick={() => deleteDetail(p.row)}
        >
          刪除
        </Button>
      ),
    }] : []),
  ],
  [canEditDetail]
);


  // ---------- 編輯送出（主表 Dialog） ----------
  const onSubmitEdit = async (evt) => {
    evt.preventDefault();
    if (!editRow) return;
    const fd = new FormData(evt.currentTarget);
    const payload = {
      id: editRow.id,
      itemName: fd.get("itemName")?.toString().trim(),
      client: fd.get("client")?.toString().trim(),
      price: fd.get("price")?.toString().trim(),
      drawNum: fd.get("drawNum")?.toString().trim(),
      currency: fd.get("currency")?.toString() || "TWD",
      user: sessionStorage.getItem("userName"),
    };

    if (!payload.itemName  || !payload.price || !payload.drawNum) {
      alert("物品名/售價/圖號 不可為空");
      return;
    }
    if (!/^\d+(\.\d+)?$/.test(payload.price)) {
      alert("售價請輸入整數或小數");
      return;
    }

    setLoading(true);
    try {
      const resp = await apiClient.post(`/SellPrice/EditItem`, payload);
      if (resp?.status === 200 || resp?.data === 200) {
        alert("修改成功");
        setEditOpen(false);
        await fetchMain();
        if (selectedDrawNum) await fetchDetail(selectedDrawNum);
      } else {
        alert("修改失敗");
      }
    } catch (e) {
      console.error(e);
      alert("修改失敗");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* 遮罩 */}
      <Backdrop sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* 查詢列 */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          售價一覽表（查詢）
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
          <TextField
            label="起始圖號"
            value={drawNum}
            onChange={(e) => setDrawNum(e.target.value)}
            onKeyDown={(e) => {
            // 避免中文輸入法組字時 Enter 被誤判
            if (e.key === "Enter" && !e.nativeEvent.isComposing) {
              commitStartToEnd(e.currentTarget.value);
            }
            }}
            onBlur={() => {
              // 每次離開起始圖號時，都以目前起始圖號覆蓋結束圖號
              commitStartToEnd(drawNum);
            }}
            fullWidth
            size="small"
          />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField inputRef={endDrawNumRef} label="結束圖號" value={enddrawNum} onChange={(e) => setendDrawNum(e.target.value)} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="物品名" value={itemName} onChange={(e) => setItemName(e.target.value)} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="客戶" value={client} onChange={(e) => setClient(e.target.value)} fullWidth size="small" />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="售價" value={price} onChange={(e) => setPrice(e.target.value)} fullWidth size="small" />
          </Grid>

          <Grid item xs={12} md={2} sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={fetchMain}>查詢</Button>
            <Button variant="outlined" onClick={exportListExcel}>匯出一覽表</Button>
            <Button variant="outlined" color="secondary" onClick={exportSingleExcel}>匯出單筆</Button>
          </Grid>
        </Grid>
      </Box>

      {/* 主表／副表：左右 70 / 30 */}
      <Grid container spacing={2} columns={{ xs: 12, md: 14 }}>
        {/* 左 70% 主表 */}
        <Grid item xs={12} md={9}>
          <Typography variant="h6" sx={{ mb: 1 }}>料品清單</Typography>
          <Box sx={{ height: 520, width: "100%" }}>
            <DataGrid
              rows={mainRows}
              columns={mainColumns}
              disableRowSelectionOnClick
              onRowDoubleClick={(p) => fetchDetail(p.row.drawNum)}
              rowHeight={38}
              headerHeight={32}
              localeText={{ noRowsLabel: "尚未搜尋或查無資料" }}
            />
          </Box>
        </Grid>

        {/* 右 30% 副表 */}
        <Grid item xs={12} md={5}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="h6">
              調價紀錄 {selectedDrawNum ? `（圖號：${selectedDrawNum}）` : ""}
            </Typography>
            {canEditDetail && (
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={saveEditedDetail}
                disabled={!pendingDetailEdit}   // 沒有變更就不能按
              >
                保存
              </Button>
            )}
          </Box>
          <Box sx={{ height: 520, width: "100%" }}>
            <DataGrid
              rows={detailRows}
              columns={detailColumns}
              editMode="cell"                       // ★ 啟用 cell 編輯模式
              disableRowSelectionOnClick
              rowHeight={38}
              headerHeight={32}
              processRowUpdate={canEditDetail ? onDetailProcessRowUpdate : undefined}
              onProcessRowUpdateError={(e) => alert(e?.message ?? "儲存失敗")}
              localeText={{ noRowsLabel: "尚未選擇圖號或此圖號無紀錄" }}
            />
          </Box>
        </Grid>
      </Grid>

      {/* 編輯小視窗（主表） */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>編輯售價資料</DialogTitle>
        <Box component="form" onSubmit={onSubmitEdit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField name="itemName" label="物品名" defaultValue={editRow?.itemName ?? ""} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField name="client" label="客戶" defaultValue={editRow?.client ?? ""} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField name="price" label="售價" defaultValue={editRow?.price ?? ""} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField name="drawNum" label="圖號" defaultValue={editRow?.drawNum ?? ""} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField name="currency" label="幣別" defaultValue={editRow?.currency ?? "TWD"} select fullWidth size="small">
                  <MenuItem value="TWD">TWD</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="CNY">CNY</MenuItem>
                  <MenuItem value="HKD">HKD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="AUD">AUD</MenuItem>
                  <MenuItem value="PHP">PHP</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>取消</Button>
            <Button type="submit" variant="contained">儲存</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
